"use client"

// Compare Bundles — Phase 4b view.
//
// Side-by-side comparison of up to 4 server-discovered bundle types. Reuses
// /api/integrations/shopify/bundle-performance via useBundlePerformance — no
// dedicated endpoint. Picker source = response.bundle_types sorted by bundle
// revenue desc; per-card bars draw from BundleTypePerformance.bundle and
// BundleTypePerformance.standalone (orchestrator's per-type slice via
// splitStandaloneByFamilyKeys).

import { useMemo, useState } from "react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useBundlePerformance } from "@/components/shopify/economics/use-bundle-performance"
import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  Info,
  Package,
  X,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { format, parse as parseDate } from "date-fns"
import type { BundleTypePerformance } from "@/lib/shopify/bundles/types"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





const MAX_SLOTS = 4
const fmtCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
const fmtInt = new Intl.NumberFormat("en-US")

const STANDALONE_OVERLAP_TOOLTIP =
  "This standalone value also appears for other bundle types whose families overlap with this one. " +
  "The same solo line items are attributed to every type that shares their families — don't sum across cards."

const BAR_TOOLTIP_BUNDLE =
  "Revenue from line_items that are part of this bundle in the selected period."

const BAR_TOOLTIP_STANDALONE =
  "Revenue from line_items of this bundle's families that were NOT bought as part of any bundle in the period."

// `starts_at` / `ends_at` are Postgres `date` columns — calendar
// literals, parsed without timezone shift.
function formatYmdLiteral(ymd: string | null): string {
  if (!ymd) return ""
  try {
    return format(parseDate(ymd, "yyyy-MM-dd", new Date()), "MMM d, yyyy")
  } catch {
    return ymd
  }
}

interface CompareBundlesViewProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  timezone: string
}

export function CompareBundlesView({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal,
  timezone,
}: CompareBundlesViewProps) {
  const { bundleTypes, isLoading, error } = useBundlePerformance({
    integrationId,
    periodStart: monthStartUtc,
    periodEnd: monthEndUtc,
    minimumOrderTotal,
    timezone,
  })

  const [showArchived, setShowArchived] = useState(false)
  const [selectedSignatures, setSelectedSignatures] = useState<(string | null)[]>([
    null,
    null,
    null,
    null,
  ])

  const visibleTypes = useMemo(
    () => bundleTypes.filter((t) => showArchived || t.ends_at == null),
    [bundleTypes, showArchived],
  )

  const sortedTypes = useMemo(
    () =>
      [...visibleTypes].sort(
        (a, b) => b.bundle.total_revenue - a.bundle.total_revenue,
      ),
    [visibleTypes],
  )

  const selectedTypes = useMemo(
    () =>
      selectedSignatures
        .map((sig) =>
          sig == null ? null : bundleTypes.find((t) => t.bundle_definition_id === sig) ?? null,
        ),
    [selectedSignatures, bundleTypes],
  )

  const hasArchived = useMemo(
    () => bundleTypes.some((t) => t.ends_at != null),
    [bundleTypes],
  )

  // Shared scale across all selected cards — bars become comparable visually.
  // Use 1 as floor so a single all-zero bundle doesn't divide by zero.
  const sharedMaxRevenue = useMemo(() => {
    let max = 0
    for (const t of selectedTypes) {
      if (t == null) continue
      if (t.bundle.total_revenue > max) max = t.bundle.total_revenue
      if (t.standalone.total_revenue > max) max = t.standalone.total_revenue
    }
    return Math.max(max, 1)
  }, [selectedTypes])

  // Standalone overlap — when two selected types share at least one family_key,
  // splitStandaloneByFamilyKeys attributes the same solo line_item to both
  // (OR-semantics: row.family_key ∈ type.family_keys). We detect overlap by
  // intersecting the family_keys sets of every pair of selected types — this
  // catches partial overlaps (e.g. `bags|holsters` vs `bags|adapters` share
  // `bags`) that a value-based bucket would miss when total revenues differ
  // due to non-shared families. Result is a Set of bundle_definition_ids that
  // share families with at least one other selected type.
  const sharedStandaloneSignatures = useMemo(() => {
    const shared = new Set<string>()
    const filled = selectedTypes.filter(
      (t): t is BundleTypePerformance => t != null,
    )
    for (let i = 0; i < filled.length; i++) {
      const aKeys = new Set(filled[i].family_keys)
      for (let j = 0; j < filled.length; j++) {
        if (i === j) continue
        if (filled[j].family_keys.some((k) => aKeys.has(k))) {
          shared.add(filled[i].bundle_definition_id)
          break
        }
      }
    }
    return shared
  }, [selectedTypes])

  const handleSelect = (slotIdx: number, signature: string | null) => {
    setSelectedSignatures((prev) => {
      const next = [...prev]
      next[slotIdx] = signature
      return next
    })
  }

  const filledSlotCount = selectedTypes.filter((t) => t != null).length
  const noTypesAtAll = !isLoading && !error && bundleTypes.length === 0

  return (
    <TooltipProvider delayDuration={300}>
      <div className="space-y-4">
        <Toolbar
          showArchived={showArchived}
          onShowArchivedChange={setShowArchived}
          archivedAvailable={hasArchived}
        />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: MAX_SLOTS }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : noTypesAtAll ? (
          <EmptyState />
        ) : (
          <>
            {/* Picker row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {selectedSignatures.map((sig, idx) => (
                <SlotPicker
                  key={idx}
                  slotIndex={idx}
                  selected={selectedTypes[idx]}
                  options={sortedTypes}
                  excludeSignatures={selectedSignatures.filter(
                    (s, i) => i !== idx && s != null,
                  ) as string[]}
                  onChange={(s) => handleSelect(idx, s)}
                />
              ))}
            </div>

            {filledSlotCount === 0 ? (
              <PickHint />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {selectedTypes.map((type, idx) =>
                  type == null ? null : (
                    <CompareCard
                      key={`${type.bundle_definition_id}-${idx}`}
                      type={type}
                      sharedMax={sharedMaxRevenue}
                      // Gate the visual hint on a non-zero standalone value:
                      // family_keys overlap can be true even when this type's
                      // standalone slice is empty in this period, but there's
                      // nothing visually meaningful to flag at $0.
                      standaloneShared={
                        sharedStandaloneSignatures.has(type.bundle_definition_id) &&
                        type.standalone.total_revenue > 0
                      }
                      onClear={() => handleSelect(idx, null)}
                    />
                  ),
                )}
              </div>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  )
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

interface ToolbarProps {
  showArchived: boolean
  onShowArchivedChange: (v: boolean) => void
  archivedAvailable: boolean
}

function Toolbar({ showArchived, onShowArchivedChange, archivedAvailable }: ToolbarProps) {
  const tUi = useTranslations("Shopify.ShopifyCrossSellCompareBundlesView")

  return (
    <div className="flex items-center justify-between">
      <div className="text-sm text-muted-foreground">
        <span><span key={tUi("pickUpTo")}>{tUi("pickUpTo")}</span></span> {MAX_SLOTS} <span><span key={tUi("bundleTypesToCompareBundleRevenueAgainstTheir")}>{tUi("bundleTypesToCompareBundleRevenueAgainstTheir")}</span></span>
      </div>
      {archivedAvailable && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={showArchived} onCheckedChange={onShowArchivedChange} />
          <span><span key={tUi("showArchived")}>{tUi("showArchived")}</span></span>
        </label>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty / hint states
// ---------------------------------------------------------------------------

function EmptyState() {
  const tUi = useTranslations("Shopify.ShopifyCrossSellCompareBundlesView")

  return (
    <div className="flex flex-col items-center justify-center h-48 text-muted-foreground gap-2">
      <Package className="h-8 w-8" />
      <p className="text-sm font-medium"><span key={tUi("noBundleTypesDetectedForThisPeriod")}>{tUi("noBundleTypesDetectedForThisPeriod")}</span></p>
      <p className="text-xs max-w-md text-center">
        <span key={tUi("compareBundlesUsesBundleTypesAutoDetectedFrom")}>{tUi("compareBundlesUsesBundleTypesAutoDetectedFrom")}</span></p>
    </div>
  )
}

function PickHint() {
  const tUi = useTranslations("Shopify.ShopifyCrossSellCompareBundlesView")

  return (
    <p className="text-sm text-muted-foreground italic">
      <span key={tUi("pickABundleTypeAboveToStartComparing")}>{tUi("pickABundleTypeAboveToStartComparing")}</span></p>
  )
}

// ---------------------------------------------------------------------------
// Slot picker (Combobox)
// ---------------------------------------------------------------------------

interface SlotPickerProps {
  slotIndex: number
  selected: BundleTypePerformance | null
  options: BundleTypePerformance[]
  /** Signatures already chosen in other slots — hidden from this picker. */
  excludeSignatures: string[]
  onChange: (signature: string | null) => void
}

function SlotPicker({
  slotIndex,
  selected,
  options,
  excludeSignatures,
  onChange,
}: SlotPickerProps) {


  const tUi = useTranslations("Shopify.ShopifyCrossSellCompareBundlesView")

  const [open, setOpen] = useState(false)
  const excluded = useMemo(() => new Set(excludeSignatures), [excludeSignatures])
  const visibleOptions = useMemo(
    () => options.filter((o) => !excluded.has(o.bundle_definition_id)),
    [options, excluded],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={tUi("bundleSlotValue0", { value0: String(slotIndex + 1) })}
          className="w-full justify-between font-normal"
        >
          <span key={selected ? selected.display_label : tUi("slotValue0PickABundle", { value0: String(slotIndex + 1) })} className="truncate text-left">
            {selected ? selected.display_label : tUi("slotValue0PickABundle", { value0: String(slotIndex + 1) })}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder={tUi("searchByNameOrFamily")} />
          <CommandList>
            <CommandEmpty><span key={tUi("noMatchingBundleTypes")}>{tUi("noMatchingBundleTypes")}</span></CommandEmpty>
            <CommandGroup>
              {selected && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onChange(null)
                    setOpen(false)
                  }}
                  className="text-muted-foreground"
                >
                  <X className="mr-2 h-4 w-4" />
                  <span><span key={tUi("clearSelection")}>{tUi("clearSelection")}</span></span>
                </CommandItem>
              )}
              {visibleOptions.map((opt) => (
                <CommandItem
                  key={opt.bundle_definition_id}
                  // CommandInput searches against `value`. Combine the bits
                  // that a user might type — custom label, signature, family
                  // keys — so search hits any of them.
                  value={`${opt.display_label} ${opt.bundle_definition_id} ${opt.family_keys.join(" ")}`}
                  onSelect={() => {
                    onChange(opt.bundle_definition_id)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0",
                      selected?.bundle_definition_id === opt.bundle_definition_id
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate font-medium">{opt.display_label}</span>
                      {opt.ends_at && (
                        <Badge variant="secondary" className="shrink-0 text-[10px]">
                          <span key={tUi("archived")}>{tUi("archived")}</span></Badge>
                      )}
                    </div>
                    <span className="truncate text-xs text-muted-foreground">
                      {opt.bundle_definition_id}
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums"><TranslationText>{tUi.rich("value0Value1Value2Orders", { value0: String(fmtCurrency.format(opt.bundle.total_revenue)), value1: String(" "), value2: String(fmtInt.format(opt.bundle.order_count)) })}</TranslationText></span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// Compare card
// ---------------------------------------------------------------------------

interface CompareCardProps {
  type: BundleTypePerformance
  /** Max revenue across all selected cards — used for fair scale. */
  sharedMax: number
  standaloneShared: boolean
  onClear: () => void
}

function CompareCard({ type, sharedMax, standaloneShared, onClear }: CompareCardProps) {

  const tUi = useTranslations("Shopify.ShopifyCrossSellCompareBundlesView")

  const bundleRev = type.bundle.total_revenue
  const standRev = type.standalone.total_revenue
  const isArchived = type.ends_at != null
  const launchedDisplay = formatYmdLiteral(type.starts_at)
  const archivedDisplay = formatYmdLiteral(type.ends_at)

  return (
    <Card className={cn(isArchived && "opacity-70")}>
      <CardHeader className="space-y-1.5 pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-sm break-words leading-tight">
              {type.display_label}
            </div>
            <div className="text-[11px] text-muted-foreground break-words">
              {type.bundle_definition_id}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0 text-muted-foreground"
            onClick={onClear}
            aria-label={tUi("removeFromComparison")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          {launchedDisplay && <span><TranslationText>{tUi.rich("launchedValue0", { value0: String(launchedDisplay) })}</TranslationText></span>}
          {isArchived && archivedDisplay && (
            <span className="italic"><TranslationText>{tUi.rich("archivedValue0", { value0: String(archivedDisplay) })}</TranslationText></span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <BarRow
          label={tUi("asBundle")}
          value={bundleRev}
          max={sharedMax}
          colorClass="bg-emerald-500"
          tooltip={BAR_TOOLTIP_BUNDLE}
        />
        <BarRow
          label={tUi("standalone")}
          value={standRev}
          max={sharedMax}
          colorClass="bg-amber-500"
          tooltip={
            standaloneShared
              ? `${BAR_TOOLTIP_STANDALONE} ${STANDALONE_OVERLAP_TOOLTIP}`
              : BAR_TOOLTIP_STANDALONE
          }
          shared={standaloneShared}
        />
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 pt-2 border-t text-xs">
          <Stat label={tUi("bundleOrders")} value={fmtInt.format(type.bundle.order_count)} />
          <Stat label={tUi("bundleUnits")} value={fmtInt.format(type.bundle.units_sold)} />
          <Stat label={tUi("standaloneUnits")} value={fmtInt.format(type.standalone.units_sold)} />
          {/* Server-computed avg_revenue_per_order = total_revenue / distinct
              order_count. NOT per bundle instance — a single order can carry
              multiple bundle groups of the same type or quantity > 1. Label
              reflects the per-order denominator to keep semantics honest. */}
          <Stat
            label={tUi("avgOrder")}
            value={
              type.bundle.order_count > 0
                ? fmtCurrency.format(type.bundle.avg_revenue_per_order)
                : "—"
            }
          />
        </div>
      </CardContent>
    </Card>
  )
}

interface BarRowProps {
  label: string
  value: number
  max: number
  colorClass: string
  tooltip: string
  shared?: boolean
}

function BarRow({ label, value, max, colorClass, tooltip, shared }: BarRowProps) {
  // Width as % of shared max. Clamp to [0, 100] — defensive against future
  // refactors that might pass a non-shared max smaller than `value`.
  const widthPct = Math.max(0, Math.min(100, (value / max) * 100))

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground inline-flex items-center gap-1">
          {label}
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 cursor-help text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[300px] text-xs">{tooltip}</TooltipContent>
          </Tooltip>
        </span>
        <span
          className={cn(
            "font-medium tabular-nums",
            shared && "italic text-muted-foreground",
          )}
        >
          {fmtCurrency.format(value)}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all", colorClass)}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
        {label}
      </span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  )
}
